/**
 * agent-rail-bridge.ts — read-only bridge to the existing agent rail.
 *
 * The agent rail (apps/zeq-dev/public/embed/agent-rail.js) is auto-injected
 * on every hosted slug page. It already lists the agents on the page,
 * tracks which one is "active", persists the choice in localStorage, and
 * intercepts page-chat POSTs to inject `agent_id`. It is connected to the
 * LLM and we are NOT to touch it.
 *
 * This bridge:
 *   1. Reads `localStorage.zeq.rail.<slug>.active` (the rail's source of truth).
 *   2. Polls for changes every 333 ms (≈3 ticks per Zeqond) so when the user
 *      clicks an agent in the rail, the Pulse orb panel knows.
 *   3. Emits `pulse:agent-changed` CustomEvent on window with the new agent id.
 *
 * No write-back to the rail. No fetch interception. The rail's existing
 * `window.fetch` wrapper does the agent_id injection on POST — we just
 * mirror its state for the orb's panel UI.
 */

const POLL_MS = 333;

interface BridgeState {
  slug: string | null;
  activeAgent: string | null;
  pollTimer: ReturnType<typeof setInterval> | null;
}

const state: BridgeState = {
  slug: null,
  activeAgent: null,
  pollTimer: null,
};

/**
 * Resolve the slug. URL first (/s/<slug>/ pages); off slug pages
 * (the standalone Root terminal at /apps/zeq-cli/, portal, anywhere)
 * fall back to the identity store — the active account, then the
 * legacy slot. P21.R8: standalone ran "@unknown · off-machine" while
 * its OWN api-client resolved keys from exactly these stores, so auth
 * worked but the surface lied about who you were.
 */
export function resolveSlug(): string | null {
  if (typeof location === "undefined") return null;
  const m = /^\/s\/([^/]+)/.exec(location.pathname);
  if (m) return m[1];
  try {
    const accounts = JSON.parse(localStorage.getItem("zeq_accounts") || "[]") as Array<{ slug?: string }>;
    const activeZid = (localStorage.getItem("zeq_active_zid") || "").toLowerCase();
    if (activeZid) {
      const hit = accounts.find((a) => a && String(a.slug || "").toLowerCase() === activeZid);
      if (hit && hit.slug) return String(hit.slug).toLowerCase();
    }
    if (accounts.length === 1 && accounts[0] && accounts[0].slug) {
      return String(accounts[0].slug).toLowerCase();
    }
  } catch { /* store unreadable — fall through */ }
  try {
    const legacy = localStorage.getItem("zeq_machine_slug");
    if (legacy) return legacy.toLowerCase();
  } catch { /* no legacy slot */ }
  return null;
}

/** Storage key used by agent-rail.js (verified in agent-rail.js source). */
function activeKey(slug: string): string {
  return `zeq.rail.${slug}.active`;
}

/** Read whatever the rail thinks is active right now. Null = none. */
export function getActiveAgent(slug?: string | null): string | null {
  const s = slug ?? state.slug ?? resolveSlug();
  if (!s) return null;
  try {
    const v = localStorage.getItem(activeKey(s));
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

/** Start polling. Idempotent. */
export function startBridge(): void {
  if (state.pollTimer) return;
  state.slug = resolveSlug();
  state.activeAgent = getActiveAgent(state.slug);

  state.pollTimer = setInterval(() => {
    if (!state.slug) {
      // Still scanning for slug — page might be a SPA that just navigated.
      const next = resolveSlug();
      if (next) state.slug = next;
      else return;
    }
    const next = getActiveAgent(state.slug);
    if (next !== state.activeAgent) {
      state.activeAgent = next;
      window.dispatchEvent(
        new CustomEvent("pulse:agent-changed", { detail: { agent_id: next, slug: state.slug } }),
      );
    }
  }, POLL_MS);

  // Cross-tab change → fires the storage event natively.
  window.addEventListener("storage", (ev) => {
    if (!state.slug || ev.key !== activeKey(state.slug)) return;
    const next = ev.newValue || null;
    if (next === state.activeAgent) return;
    state.activeAgent = next;
    window.dispatchEvent(
      new CustomEvent("pulse:agent-changed", { detail: { agent_id: next, slug: state.slug } }),
    );
  });
}

/** Stop polling. */
export function stopBridge(): void {
  if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; }
}

/**
 * Convenience: the slug for the page the orb is on. Cached after start.
 */
export function getSlug(): string | null {
  return state.slug ?? resolveSlug();
}
